import { Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang, scrollToId } from "@/i18n";
import { CONTACT, LEGAL_ROUTES } from "@/content";
import { Reveal } from "./Reveal";
import { Logo } from "./Logo";
import { contactItems, companyItems, IconLine } from "./ContactInfo";
import { resetConsent } from "@/consent";

export const CtaBand = () => {
  const { t } = useLang();
  return (
    <section data-testid="cta-band" className="relative overflow-hidden py-24 lg:py-32 grain">
      <img src="/img/industrial.jpg" alt="" className="absolute inset-0 h-full w-full object-cover grayscale brightness-[0.35]" />
      <div className="absolute inset-0 bg-[linear-gradient(110deg,rgba(69,10,10,0.92)_0%,rgba(24,5,5,0.85)_50%,rgba(5,5,5,0.7)_100%)]" />
      <Reveal className="relative max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <h2 className="font-display font-black uppercase text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tight">{t.cta.title}</h2>
          <p className="mt-6 max-w-2xl text-zinc-200 text-base md:text-lg">{t.cta.sub}</p>
        </div>
        <div className="lg:col-span-4 flex flex-col gap-3">
          <button data-testid="cta-quote-button" onClick={() => scrollToId("booking")} className="group clip-btn bg-white text-black hover:bg-red-600 hover:text-white transition-colors px-7 py-4 font-mono text-sm uppercase tracking-[0.18em] flex items-center justify-between">
            {t.cta.btn}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <a data-testid="cta-call-button" href={CONTACT.phoneHref} className="clip-btn border border-white/40 hover:border-white text-white transition-colors px-7 py-4 font-mono text-sm uppercase tracking-[0.18em] flex items-center justify-between">
            {t.cta.call}: {CONTACT.phone}<Phone className="h-4 w-4" />
          </a>
        </div>
      </Reveal>
    </section>
  );
};

const ColTitle = ({ children }) => <p className="font-mono text-xs uppercase tracking-[0.25em] text-red-500">{children}</p>;

export const Footer = () => {
  const { t } = useLang();
  const f = t.footer;
  const legal = [["privacy", LEGAL_ROUTES.privacy], ["terms", LEGAL_ROUTES.terms], ["cookies", LEGAL_ROUTES.cookies], ["contactPage", "/contact"]];
  return (
    <footer data-testid="site-footer" className="relative border-t border-white/10 bg-[#050505]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 py-16 grid sm:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-10">
        <div className="lg:col-span-3">
          <Logo testId="footer-logo" className="h-14 sm:h-16" />
          <p className="mt-6 max-w-sm text-sm text-zinc-400 leading-relaxed">{f.tagline}</p>
          <span className="mt-6 inline-flex items-center gap-2 border border-red-700/50 bg-red-950/30 px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest text-red-300" data-testid="footer-license-badge">Lic. {CONTACT.license}</span>
        </div>
        <div className="lg:col-span-4">
          <ColTitle>{f.contact}</ColTitle>
          <div className="mt-5 space-y-3">
            {contactItems().map((c) => <IconLine key={c.id} icon={c.icon} href={c.href} testId={`footer-${c.id}`}>{c.value}</IconLine>)}
          </div>
        </div>
        <div className="lg:col-span-3">
          <ColTitle>{f.id}</ColTitle>
          <div className="mt-5 space-y-3">
            {companyItems(f).map((c) => (
              <IconLine key={c.id} icon={c.icon} testId={`footer-${c.id}`}>
                <span className="block font-mono text-[11px] uppercase tracking-widest text-zinc-400">{c.label}</span>
                <span className="block text-white">{c.value}</span>
              </IconLine>
            ))}
          </div>
        </div>
        <div className="lg:col-span-2">
          <ColTitle>{f.legal}</ColTitle>
          <ul className="mt-5 space-y-3 text-sm">
            {legal.map(([k, to]) => (
              <li key={k}><Link to={to} data-testid={`footer-link-${k}`} className="text-zinc-300 hover:text-white underline-offset-4 hover:underline decoration-red-600">{f[k]}</Link></li>
            ))}
            <li><button data-testid="footer-cookie-settings" onClick={resetConsent} className="text-zinc-300 hover:text-white underline-offset-4 hover:underline decoration-red-600">{t.cookie.settings}</button></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 py-6 flex flex-col sm:flex-row justify-between gap-2 font-mono text-xs text-zinc-400">
          <span>© 2026 {CONTACT.company} {f.rights}</span>
          <span>CUI {CONTACT.cui} · {CONTACT.regcom}</span>
        </div>
      </div>
      <p aria-hidden="true" className="select-none overflow-hidden whitespace-nowrap font-display font-black uppercase text-[22vw] leading-[0.8] text-transparent [-webkit-text-stroke:1px_rgba(220,38,38,0.25)] text-center pb-4">Armat</p>
    </footer>
  );
};
